import PropTypes from "prop-types";

// store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";

// components
import RadioInput from "@/components/RadioInput/RadioInput";
import Checkbox from "@/components/Checkbox/Checkbox";
import Stepper from "@/components/Stepper/Stepper";
import SectionHeading from "@/components/SectionHeading/SectionHeading";

// -----------------------------------------------------------------------------

export default function CompNeuro({ isLinkMode }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;

	// Select state individually
	const compNeuroBrainCoverage = useStore(
		(state) => state.compNeuroBrainCoverage,
	);
	const neuronalAccuracy = useStore((state) => state.neuronalAccuracy);
	const simulationSpeed = useStore((state) => state.simulationSpeed);
	const speedFactor = useStore((state) => state.speedFactor);
	const modulatoryFactors = useStore((state) => state.modulatoryFactors);

	// Select actions individually
	const setCompNeuroBrainCoverage = useStore(
		(state) => state.setCompNeuroBrainCoverage,
	);
	const setSimulationSpeed = useStore((state) => state.setSimulationSpeed);
	const setSpeedFactor = useStore((state) => state.setSpeedFactor);
	const setNeuronalAccuracy = useStore((state) => state.setNeuronalAccuracy);
	const setModulatoryFactors = useStore((state) => state.setModulatoryFactors);

	// -----------------------------------------------------------------------------

	function handleModulatoryCheckbox(e) {
		const { checked, value } = e.target;
		if (checked) {
			setModulatoryFactors([...modulatoryFactors, value]);
		} else {
			setModulatoryFactors(modulatoryFactors.filter((v) => v !== value));
		}
	}

	// -----------------------------------------------------------------------------

	return (
		<section>
			<SectionHeading heading="Computational Neuroscience" number={4} />

			{/* Brain Coverage */}
			<fieldset className="radio-group">
				<legend>Brain Coverage</legend>

				<RadioInput
					label="Circuit"
					value="circuit"
					name="comp-neuro-brain-coverage"
					checked={compNeuroBrainCoverage === "circuit"}
					onChange={(e) => setCompNeuroBrainCoverage(e.target.value)}
				/>
				<RadioInput
					label="Brain area"
					value="brain-area"
					name="comp-neuro-brain-coverage"
					checked={compNeuroBrainCoverage === "brain-area"}
					onChange={(e) => setCompNeuroBrainCoverage(e.target.value)}
				/>
				<RadioInput
					label="Whole brain"
					value="whole-brain"
					name="comp-neuro-brain-coverage"
					checked={compNeuroBrainCoverage === "whole-brain"}
					onChange={(e) => setCompNeuroBrainCoverage(e.target.value)}
				/>
			</fieldset>

			{/* Neuronal Accuracy */}
			<div>
				<fieldset className="radio-group">
					<legend>Neuronal Accuracy</legend>

					<RadioInput
						label="Neuronal networks*"
						value="neuronal-networks"
						name="neuronal-accuracy"
						checked={neuronalAccuracy === "neuronal-networks"}
						onChange={(e) => setNeuronalAccuracy(e.target.value)}
					/>

					<RadioInput
						label="Neuronal compartments"
						value="neuronal-compartments"
						name="neuronal-accuracy"
						checked={neuronalAccuracy === "neuronal-compartments"}
						onChange={(e) => setNeuronalAccuracy(e.target.value)}
					/>
					<RadioInput
						label="Point neurons (LIF)"
						value="point-neurons"
						name="neuronal-accuracy"
						checked={neuronalAccuracy === "point-neurons"}
						onChange={(e) => setNeuronalAccuracy(e.target.value)}
					/>
				</fieldset>
				<p className="note">
					*ANNs with units like ReLU, firing rate units, no spiking.
				</p>
			</div>

			{/* Simulation speed stepper */}
			<Stepper
				label="Simulation Speed [Hz]"
				id="simulation-speed"
				value={simulationSpeed}
				min={0}
				max={10000}
				step={100}
				onChange={setSimulationSpeed}
			/>

			{/* Speed factor stepper */}
			<Stepper
				label="Slowdown / Speedup Factor"
				id="speed-factor"
				value={speedFactor}
				min={-1000}
				max={1000}
				step={100}
				onChange={setSpeedFactor}
			/>

			{/* Modulatory factors options */}
			<fieldset>
				<legend>Modulatory Factors</legend>
				<Checkbox
					label="Model different cell types"
					value="model-cell-types"
					id="model-cell-types"
					name="modulatory-factors"
					checked={modulatoryFactors.includes("model-cell-types")}
					onChange={handleModulatoryCheckbox}
				/>
				<Checkbox
					label="Model structure plasticity"
					value="model-plasticity"
					id="model-plasticity"
					name="modulatory-factors"
					checked={modulatoryFactors.includes("model-plasticity")}
					onChange={handleModulatoryCheckbox}
				/>
				<Checkbox
					label="Model neuromodulators"
					value="model-neuromodulators"
					id="model-neuromodulators"
					name="modulatory-factors"
					checked={modulatoryFactors.includes("model-neuromodulators")}
					onChange={handleModulatoryCheckbox}
				/>
			</fieldset>
		</section>
	);
}

// -----------------------------------------------------------------------------

CompNeuro.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
};
